import { routes } from '@/config/routes'
import { useLayoutContext } from '@/context/layout'
import { useMixpanel } from '@/context/mixpanel'
import { cn } from '@/utils/cn'
import type { ReactNode } from 'react'
import { NavLink, type NavLinkProps, generatePath, matchPath, useLocation } from 'react-router'
import { SidebarApiPromoCard } from '../caseStudies/sidebarApiPromoCard'
import { Button } from '../common/button'
import { MenuLink } from '../common/menuLink'
import { ScrollArea } from '../common/scrollArea'
import { CctpIcon } from '../icons/cctp'
import { LogoIcon } from '../icons/logo'
import { MoneyBagIcon } from '../icons/moneyBag'
import { PieIcon } from '../icons/pie'
import { SearchIcon } from '../icons/search'
import { WalletIcon } from '../icons/wallet'
import { XIcon } from '../icons/x'
import { ExpandNavigationButton } from './expandNavigationButton'

interface MenuLinkItem extends NavLinkProps {
  name: string
  icon: ReactNode
  activePaths?: string[]
}

export const MENU_LINKS: MenuLinkItem[] = [
  {
    name: 'Discover',
    icon: <SearchIcon filled={true} />,
    to: routes.root,
    activePaths: [routes.root, routes.fixedTerm, routes.fixedTermMarket, routes.opportunity],
  },
  { name: 'Borrow', icon: <MoneyBagIcon />, to: routes.borrow },
  {
    name: 'My positions',
    icon: <WalletIcon />,
    to: generatePath(routes.dashboard, { address: '' }),
    prefetch: 'intent',
  },
  { name: 'Analytics', icon: <PieIcon />, to: routes.analytics, prefetch: 'intent' },
  { name: 'Bridge funds', icon: <CctpIcon />, to: routes.cctp, prefetch: 'intent' },
]

export const isMenuLinkActive = (pathname: string, activePaths?: string[]) =>
  activePaths?.some((path) => matchPath(path, pathname)) ?? false

export const headerLinks = {
  twitter: 'https://x.com/vaultsfyi',
  telegram: 'https://t.me/vaultsfyisupport',
  legal: 'https://www.wallfacer.io/legal',
  api: 'https://www.vaults.fyi/api',
}

const EXTERNAL_LINKS = [
  { href: headerLinks.telegram, label: 'Help' },
  { href: headerLinks.api, label: 'API' },
  { href: headerLinks.legal, label: 'Legal' },
]

export const Header = () => {
  const { isNavbarExpanded, setNavbarPointerPreviewed } = useLayoutContext()
  const { trackEvent } = useMixpanel()
  const { pathname } = useLocation()

  return (
    <header
      onMouseEnter={() => setNavbarPointerPreviewed(true)}
      onMouseLeave={() => setNavbarPointerPreviewed(false)}
      className={cn(
        'group sticky top-0 left-0 z-10 hidden h-full max-h-full w-full flex-col overflow-hidden border-r bg-page pt-5 pb-4 transition-all xl:flex xl:[grid-area:leftBar]',
        isNavbarExpanded ? 'w-header-expanded' : 'w-header-collapsed'
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between gap-4 transition-all',
          isNavbarExpanded ? 'pl-6' : 'pl-4',
          'pr-3'
        )}
      >
        <Button
          asChild
          variant="default"
          size="default"
          className={cn(
            'h-8 justify-end text-heading-foreground [--header-logo-text-width:82px]',
            'group !ring-0 hocus:text-accent',
            !isNavbarExpanded && '-translate-x-[var(--header-logo-text-width)]'
          )}
        >
          <NavLink to={routes.root}>
            <div
              className={cn('flex w-fit items-center justify-end gap-2 text-xl', !isNavbarExpanded && '-translate-x-1')}
            >
              <span
                className={cn(
                  'w-[var(--header-logo-text-width)] transition-all',
                  isNavbarExpanded ? 'opacity-100' : 'opacity-0'
                )}
              >
                vaults.fyi
              </span>
              <div
                className={cn(
                  'ml-auto',
                  'relative flex items-center justify-center before:absolute before:size-12 before:rounded-full group-focus-visible:before:ring'
                )}
              >
                <LogoIcon size={28} />
              </div>
            </div>
          </NavLink>
        </Button>

        <ExpandNavigationButton />
      </div>

      <nav className="flex max-h-full flex-grow flex-col overflow-hidden">
        <ScrollArea className="h-full">
          <ul className="mt-5 flex flex-col gap-1">
            {MENU_LINKS.map(({ name, icon, to, prefetch, activePaths }) => (
              <li key={name + String(to)}>
                <MenuLink
                  to={to}
                  prefetch={prefetch}
                  icon={icon}
                  label={name}
                  showLabel={isNavbarExpanded}
                  className={cn(
                    '[&.active]:text-active-foreground',
                    isMenuLinkActive(pathname, activePaths) && 'active text-active-foreground'
                  )}
                  onClick={() => trackEvent('Menu link clicked', { Page: name })}
                />
              </li>
            ))}
          </ul>
        </ScrollArea>
      </nav>

      <div
        {...(!isNavbarExpanded && { inert: '' })}
        className={cn(
          'flex flex-col gap-3 px-3 transition-opacity',
          isNavbarExpanded ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      >
        <div className="w-[calc(var(--header-expanded-width)-1.5rem)] shrink-0">
          <SidebarApiPromoCard />
        </div>

        <div className="flex items-center justify-center gap-3 overflow-hidden p-3 font-medium text-body-foreground text-xs">
          {EXTERNAL_LINKS.map(({ href, label }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap rounded-lg p-2 hocus:text-accent outline-none transition-colors focus-visible:ring"
              onClick={() => trackEvent('Menu link clicked', { Page: label, Href: href })}
            >
              {label}
            </a>
          ))}
          <a
            href={headerLinks.twitter}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 rounded-full p-2 hocus:text-accent outline-none transition-colors focus-visible:ring"
            aria-label="Visit our Twitter"
            onClick={() => trackEvent('Menu link clicked', { Page: 'Twitter', Href: headerLinks.twitter })}
          >
            <XIcon size={20} />
          </a>
        </div>
      </div>
    </header>
  )
}
